import React, { useState, useEffect, useRef } from 'react';
import { 
  Plus, Trash2, Printer, Search, X, Check, 
  ArrowUpDown, ArrowUp, ArrowDown, Clock, Info, AlertTriangle,
  Send, RefreshCw, WifiOff, Ban, DatabaseBackup, Trash
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import VoucherPrintTemplate from '../components/VoucherPrintTemplate';
import MikrotikGatewaySelector from '../components/MikrotikGatewaySelector';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import { useBranding } from '../contexts/BrandingContext';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// 2026-02-03: MySQL server timezone = WIB (Asia/Jakarta), sehingga TIMESTAMP tersimpan
// dalam waktu lokal WIB — bukan UTC. Frontend browser klien juga WIB. Karena itu:
//   * Kalau string sudah mengandung 'Z' atau '+HH:MM' → parse langsung (ISO explicit TZ)
//   * Kalau string "YYYY-MM-DD HH:MM:SS" polos → treat as LOCAL time (WIB)
// Ini mencegah drift +7 jam pada tampilan sisa waktu voucher.
const _parseServerDate = (s) => {
  if (!s) return null;
  const str = String(s);
  if (str.includes('Z') || /[+-]\d{2}:?\d{2}$/.test(str)) return new Date(str);
  const m = str.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})/);
  if (!m) return new Date(str);
  return new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]);
};

const ManajemenVoucher = () => {
  const { token, user } = useAuth();
  const { selectedBranch, setSelectedBranch } = useApp();
  const { branding } = useBranding();
  const primaryColor = branding?.primary_color || '#4F46E5';
  const secondaryColor = branding?.secondary_color || '#10B981';
  
  const [vouchers, setVouchers] = useState([]);
  const [servicePlans, setServicePlans] = useState([]);
  const [branches, setBranches] = useState([]);
  const [owners, setOwners] = useState([]);
  const [activeVoucherUsernames, setActiveVoucherUsernames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingActive, setLoadingActive] = useState(false);
  
  // Expiry check data
  const [expiryData, setExpiryData] = useState(null);
  const [showExpiryModal, setShowExpiryModal] = useState(false);
  const [showRadiusModal, setShowRadiusModal] = useState(false);
  const [radiusInfo, setRadiusInfo] = useState(null);
  const [loadingRadius, setLoadingRadius] = useState(false);
  const [autoExpireDays, setAutoExpireDays] = useState(30);
  
  // Filter states
  // 2026-02-03: Baca status dari query param URL saat mount (deep-link dari dashboard cards).
  const [statusFilter, setStatusFilter] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search).get('status');
      const allowed = ['all', 'available', 'activated', 'active_online', 'used', 'expired', 'disabled'];
      if (p && allowed.includes(p)) return p;
    } catch (_e) { /* ignore */ }
    return 'all';
  });
  const [profilFilter, setProfilFilter] = useState('all');
  const [prefixFilter, setPrefixFilter] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchDebounce, setSearchDebounce] = useState('');
  
  // Pagination & Selection
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalVouchers, setTotalVouchers] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedVouchers, setSelectedVouchers] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  
  // Modals
  const [isGenerateOpen, setIsGenerateOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [vouchersToPrint, setVouchersToPrint] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('modern');
  const [selectedMikrotik, setSelectedMikrotik] = useState(null);
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [deleteAllLoading, setDeleteAllLoading] = useState(false);
  
  const isOwnerRole = user?.role?.toLowerCase() === 'owner';
  const branchFilter = selectedBranch || 'all';
  
  const [generateForm, setGenerateForm] = useState({
    quantity: 10, service_plan: '', branch_id: '', prefix: 'KH',
    code_length: 8, code_format: 'all_characters', login_method: 'password_same_as_username'
  });

  // Fetch vouchers when filters change
  useEffect(() => {
    if (!token) return;
    // 2026-08-08 FIX: AbortController pattern — cancel fetch lama saat dependency berubah.
    const controller = new AbortController();
    fetchVouchers({ signal: controller.signal });
    return () => controller.abort();
  }, [token, currentPage, itemsPerPage, statusFilter, branchFilter, searchDebounce]);

  // Initial data fetch
  useEffect(() => {
    if (token) {
      fetchServicePlans();
      fetchBranches();
      fetchOwners();
      fetchActiveVouchers();
    }
  }, [token]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounce(searchTerm);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // DISABLED - Active voucher check via MikroTik polling
  // System is PASSIVE - data comes from RADIUS database
  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     if (token) fetchActiveVouchers();
  //   }, 60000);
  //   return () => clearInterval(interval);
  // }, [token]);

  const fetchVouchers = async ({ signal = null } = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString()
      });
      
      if (statusFilter && statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      if (branchFilter && branchFilter !== 'all') {
        params.append('branch_id', branchFilter);
      }
      if (searchDebounce) {
        params.append('search', searchDebounce);
      }
      
      const response = await axios.get(`${API_URL}/api/vouchers?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000,
        signal
      });
      
      if (response.data.success) {
        setVouchers(response.data.data || []);
        setTotalVouchers(response.data.total || 0);
        setTotalPages(response.data.total_pages || 0);
      }
    } catch (error) {
      // 2026-08-08: silent ignore aborted request
      if (axios.isCancel?.(error) || error.name === 'CanceledError' || error.name === 'AbortError') {
        return;
      }
      console.error('Error fetching vouchers:', error);
      if (error.code !== 'ECONNABORTED') {
        toast.error('Gagal memuat data voucher');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveVouchers = async () => {
    setLoadingActive(true);
    try {
      // 2026-07-15 PERF: pakai endpoint ringan /api/radius/active-usernames (28ms vs 16s)
      const response = await axios.get(`${API_URL}/api/radius/active-usernames`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        const usernames = response.data.usernames || [];
        setActiveVoucherUsernames(usernames.map(u => (u || '').toUpperCase()));
      }
    } catch (error) {
      console.error('Error fetching active vouchers:', error);
    }
    finally { setLoadingActive(false); }
  };

  const fetchServicePlans = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/service-plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        const allPlans = response.data.data || [];
        // Store all plans for validity lookup
        setServicePlans(allPlans);
        console.log('Service plans loaded:', allPlans.length, 'hotspot:', allPlans.filter(p => p.package_type === 'hotspot').length);
      }
    } catch (error) {
      console.error('Error fetching service plans:', error);
    }
  };

  // Filter hotspot plans for dropdown
  const hotspotPlans = servicePlans.filter(p => p.package_type === 'hotspot');

  const fetchBranches = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/branches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) setBranches(response.data.data || []);
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const fetchOwners = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setOwners(response.data.data.filter(u => u.role === 'owner'));
      }
    } catch (error) {
      console.error('Error fetching owners:', error);
    }
  };

  // Note: applyFilters is no longer needed as filtering is handled by getFilteredVouchers()

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <ArrowUpDown className="w-3 h-3 ml-0.5 opacity-40" />;
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-0.5 text-green-600 dark:text-green-400" />
      : <ArrowDown className="w-3 h-3 ml-0.5 text-green-600 dark:text-green-400" />;
  };

  const handleGenerate = async () => {
    if (!generateForm.service_plan) { toast.error('Pilih paket hotspot'); return; }
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/vouchers/generate`, generateForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        toast.success(`Berhasil generate ${generateForm.quantity} voucher`);
        setIsGenerateOpen(false);
        fetchVouchers();
      }
    } catch (error) {
      toast.error('Gagal generate voucher');
    } finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    try {
      const response = await axios.delete(`${API_URL}/api/vouchers/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) { toast.success('Voucher dihapus'); fetchVouchers(); }
    } catch (error) { toast.error('Gagal menghapus voucher'); }
  };

  const handleDisable = async (voucher) => {
    try {
      const newStatus = voucher.status === 'disabled' ? 'available' : 'disabled';
      const response = await axios.put(`${API_URL}/api/vouchers/${voucher.id}/status`, 
        { status: newStatus }, { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success(newStatus === 'disabled' ? 'Voucher dinonaktifkan' : 'Voucher diaktifkan');
        fetchVouchers();
      }
    } catch (error) { toast.error('Gagal mengubah status'); }
  };

  const handleBulkDelete = async () => {
    setLoading(true);
    let count = 0;
    for (const id of selectedVouchers) {
      try {
        await axios.delete(`${API_URL}/api/vouchers/${id}`, { headers: { Authorization: `Bearer ${token}` } });
        count++;
      } catch (e) {
        console.error('Error deleting voucher:', e);
      }
    }
    toast.success(`${count} voucher dihapus`);
    setSelectedVouchers([]);
    setShowDeleteConfirm(false);
    fetchVouchers();
    setLoading(false);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedVouchers(displayedVouchers.map(v => v.id));
    } else {
      setSelectedVouchers([]);
    }
  };

  const handlePrint = (voucher) => { setVouchersToPrint([voucher]); setShowPrintModal(true); };
  const handleBulkPrint = () => {
    if (!selectedVouchers.length) { toast.error('Pilih voucher'); return; }
    setVouchersToPrint(vouchers.filter(v => selectedVouchers.includes(v.id)));
    setShowPrintModal(true);
  };

  // Check expiry status for all vouchers
  const checkExpiryStatus = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/vouchers/expiry-check`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setExpiryData(response.data.data);
        setShowExpiryModal(true);
      }
    } catch (error) {
      toast.error('Gagal cek status expiry');
    } finally {
      setLoading(false);
    }
  };

  // Check voucher RADIUS status
  const checkRadiusStatus = async (voucher) => {
    setLoadingRadius(true);
    setRadiusInfo(null);
    setShowRadiusModal(true);
    try {
      const response = await axios.get(`${API_URL}/api/vouchers/${voucher.id}/radius-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setRadiusInfo(response.data.data);
      }
    } catch (error) {
      toast.error('Gagal cek status RADIUS');
    } finally {
      setLoadingRadius(false);
    }
  };

  // Auto expire old vouchers
  const runAutoExpire = async () => {
    if (!window.confirm(`Yakin ingin auto-expire voucher yang tidak dipakai lebih dari ${autoExpireDays} hari?`)) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/vouchers/auto-expire?days_threshold=${autoExpireDays}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        toast.success(response.data.message);
        fetchVouchers();
        setShowExpiryModal(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal auto-expire');
    } finally {
      setLoading(false);
    }
  };

  // Disable expired vouchers in RADIUS (prevent login)
  const disableExpiredInRadius = async () => {
    if (!window.confirm('Blokir voucher expired di RADIUS?\n\nIni akan:\n- Cari voucher yang sudah expired (melewati masa berlaku)\n- Tambahkan Auth-Type := Reject di RADIUS\n- Voucher tidak bisa dipakai login lagi\n\nLanjutkan?')) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/vouchers/disable-expired`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        const count = response.data.data?.disabled_count || 0;
        if (count > 0) {
          toast.success(`✅ ${count} voucher expired di-disable di RADIUS`);
        } else {
          toast.info('Tidak ada voucher expired yang perlu di-disable');
        }
        fetchVouchers();
        fetchActiveVouchers();
        setShowExpiryModal(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal disable voucher expired');
    } finally {
      setLoading(false);
    }
  };

  // Disconnect individual voucher from hotspot
  const disconnectVoucher = async (username, routerId) => {
    if (!window.confirm(`Disconnect voucher ${username} dari hotspot?`)) return;
    
    try {
      const response = await axios.post(
        `${API_URL}/api/hotspot/disconnect/${routerId}/${username}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(`✅ Voucher ${username} berhasil di-disconnect`);
        fetchActiveVouchers();
      } else {
        toast.error(response.data.message || 'Gagal disconnect');
      }
    } catch (error) {
      console.error('Error disconnecting voucher:', error);
      toast.error(error.response?.data?.detail || 'Gagal disconnect voucher');
    }
  };

  // Disable and disconnect expired voucher
  const disableAndDisconnectVoucher = async (username, routerId) => {
    if (!window.confirm(`Blokir dan disconnect voucher ${username}?\n\nIni akan:\n- Disable di RADIUS (tidak bisa login lagi)\n- Disconnect session aktif\n\nLanjutkan?`)) return;
    
    setLoading(true);
    try {
      // Step 1: Disable in RADIUS
      await axios.post(
        `${API_URL}/api/radius/users/${username}/disable`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Step 2: Disconnect from hotspot if router_id available
      if (routerId) {
        await axios.post(
          `${API_URL}/api/hotspot/disconnect/${routerId}/${username}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        ).catch(() => {}); // Ignore error if already disconnected
      }
      
      toast.success(`✅ Voucher ${username} di-disable dan di-disconnect`);
      fetchVouchers();
      fetchActiveVouchers();
    } catch (error) {
      console.error('Error disabling voucher:', error);
      toast.error(error.response?.data?.detail || 'Gagal disable voucher');
    } finally {
      setLoading(false);
    }
  };

  // Sync vouchers from RADIUS
  const syncFromRadius = async () => {
    setSyncLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/vouchers/sync-from-radius`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        const { synced_count, already_synced, radius_users_with_sessions } = response.data;
        toast.success(`✅ Sync selesai: ${synced_count} voucher diupdate, ${already_synced} sudah sync`);
        fetchVouchers();
      }
    } catch (error) {
      console.error('Error syncing from RADIUS:', error);
      toast.error(error.response?.data?.detail || 'Gagal sync dari RADIUS');
    } finally {
      setSyncLoading(false);
    }
  };

  // Delete ALL vouchers
  const deleteAllVouchers = async () => {
    setDeleteAllLoading(true);
    try {
      const response = await axios.delete(`${API_URL}/api/vouchers/delete-all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        const { mongodb_deleted, radius_radcheck_deleted } = response.data;
        toast.success(`✅ Berhasil hapus semua voucher: MongoDB=${mongodb_deleted}, RADIUS=${radius_radcheck_deleted}`);
        setShowDeleteAllConfirm(false);
        fetchVouchers();
      }
    } catch (error) {
      console.error('Error deleting all vouchers:', error);
      toast.error(error.response?.data?.detail || 'Gagal menghapus semua voucher');
    } finally {
      setDeleteAllLoading(false);
    }
  };

  const resetFilters = () => {
    setStatusFilter('all'); setProfilFilter('all'); setPrefixFilter('');
    setOwnerFilter('all'); setDateFrom(''); setDateTo(''); setSearchTerm('');
  };

  const getStatusBadge = (voucher) => {
    const isOnline = activeVoucherUsernames.includes(voucher.username?.toUpperCase());
    if (isOnline) return <span className="inline-block min-w-[80px] px-2 py-0.5 text-[10px] font-semibold rounded text-center whitespace-nowrap bg-green-500 text-white">ONLINE</span>;
    
    // Admin-disabled (Auth-Type := Reject) takes visual priority over status
    if (voucher.radius_disabled) {
      return (
        <span
          className="inline-flex items-center justify-center min-w-[80px] gap-1 px-2 py-0.5 text-[10px] font-semibold rounded text-center whitespace-nowrap bg-red-50 text-red-700 border border-red-200 dark:bg-rose-900/40 dark:text-rose-300 dark:border-rose-800"
          title="Login voucher ini dinonaktifkan admin"
          data-testid="voucher-status-disabled"
        >
          Disabled
        </span>
      );
    }
    
    const cfg = {
      available: { bg: 'bg-blue-50 text-blue-700 border-blue-200', label: 'Belum Aktif' },
      used: { bg: 'bg-green-50 text-green-700 border-green-200', label: 'Terpakai' },
      expired: { bg: 'bg-gray-100 text-gray-500 border-gray-200', label: 'Expired' },
      disabled: { bg: 'bg-red-50 text-red-700 border-red-200', label: 'Disabled' }
    };
    const c = cfg[voucher.status] || cfg.available;
    return <span className={`inline-block min-w-[80px] px-2 py-0.5 text-[10px] font-semibold rounded text-center whitespace-nowrap border ${c.bg}`}>{c.label}</span>;
  };

  // Status badge with theme color for ONLINE
  // 2026-02-03 FIX: Sebelumnya ONLINE pakai primaryColor tenant — di beberapa branding
  // primaryColor gelap/hitam sehingga badge tidak terbaca. Gunakan warna emerald yang
  // universal untuk status online, dengan indikator dot animasi supaya jelas di light & dark theme.
  const getThemedStatusBadge = (voucher) => {
    const isOnline = activeVoucherUsernames.includes(voucher.username?.toUpperCase());
    if (isOnline) {
      return (
        <span
          className="inline-flex items-center gap-1 min-w-[80px] justify-center px-2 py-0.5 text-[10px] font-semibold rounded-full text-center whitespace-nowrap bg-transparent border border-emerald-500 text-emerald-600 dark:text-emerald-400 dark:border-emerald-400"
          data-testid="voucher-status-online"
        >
          <span className="relative flex h-1.5 w-1.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75 dark:bg-emerald-400"></span>
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500 dark:bg-emerald-400"></span>
          </span>
          ONLINE
        </span>
      );
    }
    return getStatusBadge(voucher);
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('id-ID') : '-';
  
  // Get validity info from voucher or lookup from service plan
  const getValidityInfo = (v) => {
    // First check if voucher has validity_days and validity_unit (MySQL fields)
    if (v.validity_days && v.validity_unit) {
      const period = v.validity_days;
      const unit = v.validity_unit;
      let seconds = period * 86400; // default days
      
      if (unit === 'hours') seconds = period * 3600;
      else if (unit === 'days') seconds = period * 86400;
      else if (unit === 'months') seconds = period * 30 * 86400;
      else if (unit === 'weeks') seconds = period * 7 * 86400;
      
      return { period, unit, seconds };
    }
    
    // Fallback: check validity_period (legacy field)
    if (v.validity_period && v.validity_unit) {
      const period = v.validity_period;
      const unit = v.validity_unit;
      let seconds = period * 86400;
      
      if (unit === 'hours') seconds = period * 3600;
      else if (unit === 'days') seconds = period * 86400;
      else if (unit === 'months') seconds = period * 30 * 86400;
      else if (unit === 'weeks') seconds = period * 7 * 86400;
      
      return { period, unit, seconds };
    }
    
    // Lookup from service plan by name
    const plan = servicePlans.find(p => p.name === v.service_plan || p.name === v.package_name);
    if (plan && plan.validity_period && plan.validity_unit) {
      const period = plan.validity_period;
      const unit = plan.validity_unit;
      let seconds = period * 86400;
      
      if (unit === 'hours') seconds = period * 3600;
      else if (unit === 'days') seconds = period * 86400;
      else if (unit === 'months') seconds = period * 30 * 86400;
      else if (unit === 'weeks') seconds = period * 7 * 86400;
      
      return { period, unit, seconds };
    }
    
    // Ultimate fallback
    const period = v.validity_days || 1;
    return { period, unit: 'days', seconds: period * 86400 };
  };
  
  // Get validity in seconds
  const getValiditySeconds = (v) => {
    return getValidityInfo(v).seconds;
  };
  
  // Format validity display
  const formatValidity = (v) => {
    const info = getValidityInfo(v);
    
    if (info.unit === 'hours') return `${info.period} jam`;
    if (info.unit === 'days') return `${info.period} hari`;
    if (info.unit === 'months') return `${info.period} bulan`;
    if (info.unit === 'weeks') return `${info.period} minggu`;
    return `${info.period} ${info.unit}`;
  };
  
  const calcExpiry = (v) => {
    const validityInfo = getValidityInfo(v);
    const validitySeconds = validityInfo.seconds;
    const validityUnit = validityInfo.unit;
    
    // Check if voucher is currently online
    const isOnline = activeVoucherUsernames.includes(v.username?.toUpperCase());
    
    // If voucher status is already "expired" in database
    if (v.status === 'expired') {
      if (v.expired_at) {
        // 2026-02-03 FIX: MySQL server timezone = WIB (Asia/Jakarta). Nilai TIMESTAMP tersimpan sebagai WIB,
        // bukan UTC. Jangan paksa ' + Z' — parse sebagai local time supaya konsisten dengan browser
        // client yang juga WIB (menghindari drift +7 jam pada tampilan "sisa/expired").
        const expiredDate = _parseServerDate(v.expired_at);
        const now = new Date();
        const daysSince = Math.floor((now - expiredDate) / 86400000);
        const hoursSince = Math.floor((now - expiredDate) / 3600000);
        if (validityUnit === 'hours' || hoursSince < 24) {
          return <span className="text-red-500 text-xs font-medium">Expired ({hoursSince}h lalu)</span>;
        }
        return <span className="text-red-500 text-xs font-medium">Expired ({daysSince}d lalu)</span>;
      }
      return <span className="text-red-500 text-xs font-medium">Expired</span>;
    }
    
    // For vouchers with used_at (status "used" or synced from RADIUS)
    if (v.used_at && validitySeconds > 0) {
      // 2026-02-03 FIX: server MySQL WIB, jadi parse used_at sebagai local (WIB), bukan UTC.
      const usedDate = _parseServerDate(v.used_at);
      const expDate = new Date(usedDate.getTime() + validitySeconds * 1000);
      const now = new Date();
      
      if (expDate < now) {
        // Already expired
        const secondsSince = Math.floor((now - expDate) / 1000);
        if (validityUnit === 'hours') {
          const hoursSince = Math.floor(secondsSince / 3600);
          return <span className="text-red-500 text-xs font-medium">Expired ({hoursSince}h)</span>;
        }
        const daysSince = Math.floor(secondsSince / 86400);
        return <span className="text-red-500 text-xs font-medium">Expired ({daysSince}d)</span>;
      }
      
      // Calculate remaining time
      const remainingMs = expDate - now;
      const remainingSeconds = Math.floor(remainingMs / 1000);
      const days = Math.floor(remainingSeconds / 86400);
      const hours = Math.floor((remainingSeconds % 86400) / 3600);
      const minutes = Math.floor((remainingSeconds % 3600) / 60);
      
      // For hour-based vouchers, show hours and minutes
      if (validityUnit === 'hours') {
        const totalHours = days * 24 + hours;
        if (totalHours < 1) {
          return <span className="text-red-500 text-xs font-medium">{minutes}m tersisa!</span>;
        } else if (totalHours < 3) {
          return <span className="text-orange-500 text-xs font-medium">{totalHours}h {minutes}m</span>;
        }
        return <span className="text-green-600 text-xs dark:text-green-400">{totalHours}h tersisa</span>;
      }
      
      // For day-based vouchers
      if (days > 3) {
        return <span className="text-green-600 text-xs dark:text-green-400">{days}d tersisa</span>;
      } else if (days > 0) {
        return <span className="text-orange-500 text-xs font-medium">{days}d {hours}h</span>;
      } else {
        return <span className="text-red-500 text-xs font-medium">{hours}h tersisa!</span>;
      }
    }
    
    // For vouchers currently ONLINE but used_at not synced yet
    // Show that it's active and waiting for sync
    if (isOnline && !v.used_at) {
      return <span className="text-blue-500 text-xs font-medium">Aktif (sync...)</span>;
    }
    
    // For unused vouchers, show age
    if (v.status === 'available' && v.created_at && !isOnline) {
      const created = new Date(v.created_at);
      const age = Math.floor((new Date() - created) / 86400000);
      if (age > 30) {
        return <span className="text-orange-500 text-xs">{age}d lama</span>;
      }
    }
    
    return <span className="text-gray-400 text-xs">-</span>;
  };

  const stats = {
    total: vouchers.length,
    // Belum Aktif = status available DAN tidak sedang online
    available: vouchers.filter(v => v.status === 'available' && !activeVoucherUsernames.includes(v.username?.toUpperCase())).length,
    activated: vouchers.filter(v => v.status === 'used').length,
    online: vouchers.filter(v => activeVoucherUsernames.includes(v.username?.toUpperCase())).length,
    disabled: vouchers.filter(v => v.status === 'disabled').length,
    expired: vouchers.filter(v => v.status === 'expired').length
  };

  // Apply client-side filters for additional filters (profil, owner, date, etc)
  const getFilteredVouchers = () => {
    let filtered = [...vouchers];
    
    // Additional client-side filters
    if (profilFilter !== 'all') filtered = filtered.filter(v => v.service_plan === profilFilter);
    if (prefixFilter) filtered = filtered.filter(v => v.voucher_code?.toLowerCase().startsWith(prefixFilter.toLowerCase()));
    if (ownerFilter !== 'all') filtered = filtered.filter(v => v.owner_id === ownerFilter);
    if (dateFrom) filtered = filtered.filter(v => v.created_at?.split('T')[0] >= dateFrom);
    if (dateTo) filtered = filtered.filter(v => v.created_at?.split('T')[0] <= dateTo);
    
    // Online status filter (client-side)
    if (statusFilter === 'active_online') {
      filtered = filtered.filter(v => activeVoucherUsernames.includes(v.username?.toUpperCase()));
    } else if (statusFilter === 'activated') {
      filtered = filtered.filter(v => 
        v.status === 'used' && !activeVoucherUsernames.includes(v.username?.toUpperCase())
      );
    }
    
    // Sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aVal = a[sortConfig.key], bVal = b[sortConfig.key];
        // Custom: Expired column — sort by expires_at/end_time/end_date timestamp
        if (sortConfig.key === '_expired_sort') {
          const getExp = (v) => {
            const raw = v.expires_at || v.end_time || v.end_date || v.expiry_date || null;
            if (!raw) return 0;
            const t = new Date(raw).getTime();
            return isNaN(t) ? 0 : t;
          };
          aVal = getExp(a); bVal = getExp(b);
        }
        // Custom: Status — order priority: online > available > used > expired > disabled
        if (sortConfig.key === '_status_sort') {
          const prio = (v) => {
            const isOnline = activeVoucherUsernames.includes(v.username?.toUpperCase());
            if (isOnline) return 0;
            const map = { available: 1, used: 2, expired: 3, disabled: 4 };
            return map[v.status] ?? 99;
          };
          aVal = prio(a); bVal = prio(b);
        }
        if (sortConfig.key === 'price') { aVal = Number(aVal) || 0; bVal = Number(bVal) || 0; }
        if (typeof aVal === 'string') aVal = aVal?.toLowerCase() || '';
        if (typeof bVal === 'string') bVal = bVal?.toLowerCase() || '';
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return filtered;
  };
  
  const filteredVouchers = getFilteredVouchers();
  const displayedVouchers = filteredVouchers; // No client-side pagination, server handles it

  return (
    <div className="p-2 lg:p-4 bg-gray-50 min-h-screen text-[11px] dark:bg-slate-900">
      {/* Header - Soft white card with colored accent (mobile-friendly) */}
      <div className="px-3 sm:px-4 py-2.5 sm:py-3 rounded-t-lg bg-white border border-b-0 border-slate-200 border-l-4 border-l-indigo-500 flex items-center justify-between gap-2 shadow-sm dark:bg-slate-800 dark:border-slate-700">
        <div className="flex items-center gap-2 min-w-0">
          <div className="shrink-0 w-8 h-8 rounded-lg bg-indigo-50 ring-1 ring-indigo-100 flex items-center justify-center dark:bg-indigo-900/30">
            <DatabaseBackup className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
          </div>
          <span className="font-semibold text-sm text-slate-800 tracking-tight truncate dark:text-slate-200">Manajemen Voucher</span>
        </div>
        <div className="flex gap-1.5 sm:gap-2 justify-end shrink-0">
          <Button
            onClick={syncFromRadius}
            disabled={syncLoading}
            size="sm"
            className="bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 h-8 px-2 sm:px-2.5 text-xs shadow-none transition-colors dark:bg-indigo-900/30 dark:hover:bg-indigo-900/40 dark:text-indigo-300"
            data-testid="sync-radius-btn"
            title="Sync RADIUS"
          >
            <DatabaseBackup className={`w-3.5 h-3.5 sm:mr-1 ${syncLoading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">{syncLoading ? 'Syncing...' : 'Sync RADIUS'}</span>
          </Button>
          <Button
            onClick={checkExpiryStatus}
            size="sm"
            className="bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 h-8 px-2 sm:px-2.5 text-xs shadow-none transition-colors dark:bg-amber-900/30 dark:hover:bg-amber-900/40 dark:text-amber-300"
            title="Cek Expiry"
          >
            <Clock className="w-3.5 h-3.5 sm:mr-1" />
            <span className="hidden sm:inline">Cek Expiry</span>
          </Button>
          <Button
            onClick={() => setShowDeleteAllConfirm(true)}
            size="sm"
            className="bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 h-8 px-2 sm:px-2.5 text-xs shadow-none transition-colors dark:bg-rose-900/30 dark:hover:bg-rose-900/40 dark:text-rose-300"
            data-testid="delete-all-vouchers-btn"
            title="Hapus Semua"
          >
            <Trash className="w-3.5 h-3.5 sm:mr-1" />
            <span className="hidden sm:inline">Hapus Semua</span>
          </Button>
        </div>
      </div>

      {/* Filter Panel */}
      <Card className="rounded-t-none border-t-0 mb-3">
        <CardContent className="p-3">
          <div className="flex items-center gap-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5 mb-3 dark:bg-amber-900/30">
            <Info className="w-3.5 h-3.5 text-amber-600 shrink-0 dark:text-amber-400" />
            <span>Atur filter lalu klik <b className="text-slate-900 dark:text-slate-100">Tampilkan Data</b> untuk menampilkan voucher.</span>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mb-3">
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              className="h-8 text-xs border rounded px-2">
              <option value="all">Status: Semua</option>
              <option value="available">Belum Aktif</option>
              <option value="activated">Sudah Diaktifkan</option>
              <option value="active_online">Online</option>
              <option value="disabled">Disabled</option>
              <option value="expired">Expired</option>
            </select>
            
            <select value={profilFilter} onChange={(e) => setProfilFilter(e.target.value)}
              className="h-8 text-xs border rounded px-2">
              <option value="all">Profil: Semua</option>
              {hotspotPlans.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
            </select>
            
            <Input placeholder="Prefix..." value={prefixFilter} onChange={(e) => setPrefixFilter(e.target.value)}
              className="h-8 text-xs" />
            
            <select value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)}
              className="h-8 text-xs border rounded px-2" disabled={isOwnerRole}>
              <option value="all">Owner: Semua</option>
              {owners.map(o => <option key={o.id} value={o.id}>{o.full_name}</option>)}
            </select>
            
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              className="h-8 text-xs" />
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              className="h-8 text-xs" />
          </div>

          <div className="flex flex-wrap gap-2">
            <Button onClick={resetFilters} variant="outline" size="sm" className="h-8 text-xs">
              <X className="w-4 h-4 mr-1" /> Reset
            </Button>
            <Button 
              onClick={() => { setCurrentPage(1); fetchVouchers(); }} 
              size="sm" 
              className="h-8 text-xs text-white"
              style={{ backgroundColor: primaryColor }}
            >
              <Search className="w-4 h-4 mr-1" /> Tampilkan
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              className="h-8 text-xs border"
              style={{ color: primaryColor, borderColor: primaryColor }}
            >
              <Send className="w-4 h-4 mr-1" /> WhatsApp
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stats Row - Mobile Friendly */}
      <div className="grid grid-cols-3 lg:grid-cols-6 gap-2 mb-3">
        {[
          { label: 'Total', value: stats.total, color: 'border-slate-400 text-slate-700' },
          { label: 'Belum Aktif', value: stats.available, color: 'border-blue-400 text-blue-600' },
          { label: 'Teraktivasi', value: stats.activated, color: 'border-green-400 text-green-600' },
          { label: 'Online', value: stats.online, color: 'border-emerald-400 text-emerald-600', icon: loadingActive },
          { label: 'Disabled', value: stats.disabled, color: 'border-red-400 text-red-600' },
          { label: 'Expired', value: stats.expired, color: 'border-gray-300 text-gray-500' },
        ].map((s, i) => (
          <div key={i} className={`bg-white border-l-2 ${s.color} px-2 py-1 rounded shadow-sm`}>
            <div className="text-[10px] text-gray-500 flex items-center gap-1 dark:text-slate-400">
              {s.label} {s.icon && <RefreshCw className="w-3 h-3 animate-spin" />}
            </div>
            <div className={`text-sm font-bold ${s.color.split(' ')[1]}`}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Table Controls */}
      <div className="bg-white border rounded-t px-3 py-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 dark:bg-slate-800">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-gray-500 dark:text-slate-400">Show</span>
          <select value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
            className="h-7 text-xs border rounded px-2">
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
            <option value={500}>500</option>
            <option value={1000}>1000</option>
            <option value={2000}>2000</option>
            <option value={3000}>3000</option>
          </select>
          {selectedVouchers.length > 0 && (
            <>
              <Button onClick={handleBulkPrint} variant="outline" size="sm" className="h-7 text-xs text-blue-600 border-blue-300 dark:text-blue-400">
                <Printer className="w-4 h-4 mr-1" /> Print ({selectedVouchers.length})
              </Button>
              <Button onClick={() => setShowDeleteConfirm(true)} variant="destructive" size="sm" className="h-7 text-xs">
                <Trash2 className="w-4 h-4 mr-1" /> Hapus
              </Button>
            </>
          )}
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <span className="text-xs text-gray-500 dark:text-slate-400">Search:</span>
          <Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} 
            className="h-7 flex-1 sm:w-48 text-xs" placeholder="Cari..." />
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="bg-white border border-t-0 rounded-b py-12 dark:bg-slate-800">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-4 border-green-600 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-sm text-slate-500 dark:text-slate-400">Memuat data voucher...</span>
          </div>
        </div>
      )}

      {/* Mobile Card View */}
      {!loading && (
      <div className="lg:hidden space-y-3 mt-3">
        {displayedVouchers.length === 0 ? (
          <div className="bg-white rounded-lg p-6 text-center text-gray-500 dark:bg-slate-800 dark:text-slate-400">
            Tidak ada data voucher
          </div>
        ) : (
          displayedVouchers.map((v, i) => (
            <div key={v.id} className="bg-white rounded-lg border shadow-sm p-4 dark:bg-slate-800">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <div className="text-sm font-bold text-blue-600 dark:text-blue-400">{v.username}</div>
                  <div className="text-xs text-gray-500 font-mono dark:text-slate-400">{v.password}</div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  {getThemedStatusBadge(v)}
                  {calcExpiry(v)}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                <div>
                  <span className="text-gray-500 dark:text-slate-400">Profil:</span>
                  <span className="ml-1 font-medium">{v.service_plan}</span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-slate-400">Harga:</span>
                  <span className="ml-1 font-medium text-green-600 dark:text-green-400">Rp.{v.price?.toLocaleString('id-ID')}</span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-slate-400">Server:</span>
                  <span className="ml-1">{v.branch_name || 'All'}</span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-slate-400">Dibuat:</span>
                  <span className="ml-1">{formatDate(v.created_at)}</span>
                </div>
              </div>
              
              <div className="flex justify-end gap-2 pt-2 border-t">
                <button onClick={() => checkRadiusStatus(v)} className="p-2 text-purple-500 hover:bg-purple-50 rounded dark:hover:bg-purple-900/30" title="Cek RADIUS">
                  <Info className="w-5 h-5" />
                </button>
                <button onClick={() => handlePrint(v)} className="p-2 text-blue-500 hover:bg-blue-50 rounded dark:hover:bg-blue-900/30" title="Print">
                  <Printer className="w-5 h-5" />
                </button>
                {activeVoucherUsernames.includes(v.username?.toUpperCase()) && (
                  <button onClick={() => disableAndDisconnectVoucher(v.username, v.router_id)} 
                    className="p-2 text-red-600 hover:bg-red-50 rounded dark:text-red-400 dark:hover:bg-red-900/30" title="Disconnect">
                    <WifiOff className="w-5 h-5" />
                  </button>
                )}
                {v.status === 'available' && (
                  <>
                    <button onClick={() => handleDisable(v)} className="p-2 text-orange-500 hover:bg-orange-50 rounded dark:hover:bg-orange-900/30" title="Disable">
                      <Ban className="w-5 h-5" />
                    </button>
                    <button onClick={() => handleDelete(v.id)} className="p-2 text-red-500 hover:bg-red-50 rounded dark:hover:bg-red-900/30" title="Hapus">
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </>
                )}
                {v.status === 'disabled' && (
                  <button onClick={() => handleDisable(v)} className="p-2 text-green-500 hover:bg-green-50 rounded dark:hover:bg-green-900/30" title="Enable">
                    <Check className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
      )}

      {/* Desktop Table View */}
      {!loading && (
      <div className="hidden lg:block bg-white border border-t-0 rounded-b overflow-x-auto dark:bg-slate-800">
        <table className="w-full">
          <thead className="bg-gray-50 border-b text-[11px] text-gray-600 font-medium dark:bg-slate-900 dark:text-slate-300">
            <tr>
              <th className="w-10 px-2 py-1">
                <input type="checkbox" onChange={handleSelectAll} className="w-4 h-4"
                  checked={displayedVouchers.length > 0 && displayedVouchers.every(v => selectedVouchers.includes(v.id))} />
              </th>
              <th className="px-2 py-1 text-left w-10">#</th>
              <th className="px-2 py-1 text-left cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-700" onClick={() => handleSort('username')}>
                <span className="flex items-center">Username{getSortIcon('username')}</span>
              </th>
              <th className="px-2 py-1 text-left">Password</th>
              <th className="px-2 py-1 text-left cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-700" onClick={() => handleSort('service_plan')}>
                <span className="flex items-center">Profil{getSortIcon('service_plan')}</span>
              </th>
              <th className="px-2 py-1 text-left cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-700" onClick={() => handleSort('price')}>
                <span className="flex items-center">Harga{getSortIcon('price')}</span>
              </th>
              <th className="px-2 py-1 text-left">Server</th>
              <th className="px-2 py-1 text-left cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-700" onClick={() => handleSort('created_at')}>
                <span className="flex items-center">Dibuat{getSortIcon('created_at')}</span>
              </th>
              <th className="px-2 py-1 text-left cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-700" onClick={() => handleSort('_expired_sort')}>
                <span className="flex items-center">Expired{getSortIcon('_expired_sort')}</span>
              </th>
              <th className="px-2 py-1 text-left cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-700" onClick={() => handleSort('owner_name')}>
                <span className="flex items-center">Owner{getSortIcon('owner_name')}</span>
              </th>
              <th className="px-2 py-1 text-center w-32 whitespace-nowrap cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-700" onClick={() => handleSort('_status_sort')}>
                <span className="flex items-center justify-center">Status{getSortIcon('_status_sort')}</span>
              </th>
              <th className="px-2 py-1 text-center w-28">Aksi</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 text-[11px] dark:divide-slate-700">
            {displayedVouchers.map((v, i) => (
              <tr key={v.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50">
                <td className="px-2 py-1">
                  <input type="checkbox" className="w-4 h-4"
                    checked={selectedVouchers.includes(v.id)}
                    onChange={() => setSelectedVouchers(prev => 
                      prev.includes(v.id) ? prev.filter(x => x !== v.id) : [...prev, v.id]
                    )} />
                </td>
                <td className="px-2 py-1 text-gray-400">{(currentPage-1)*itemsPerPage+i+1}</td>
                <td className="px-2 py-1 text-blue-600 font-semibold dark:text-blue-400">{v.username}</td>
                <td className="px-2 py-1 font-mono text-gray-600 dark:text-slate-300">{v.password}</td>
                <td className="px-2 py-1 text-gray-700 dark:text-slate-200">{v.service_plan}</td>
                <td className="px-2 py-1 text-gray-700 font-medium dark:text-slate-200">Rp.{v.price?.toLocaleString('id-ID')}</td>
                <td className="px-2 py-1 text-gray-500 dark:text-slate-400">{v.branch_name || 'All'}</td>
                <td className="px-2 py-1 text-gray-500 dark:text-slate-400">{formatDate(v.created_at)}</td>
                <td className="px-2 py-1">{calcExpiry(v)}</td>
                <td className="px-2 py-1 text-gray-500 dark:text-slate-400">{v.owner_name || '-'}</td>
                <td className="px-2 py-1 text-center"><div className="inline-flex justify-center whitespace-nowrap">{getThemedStatusBadge(v)}</div></td>
                <td className="px-2 py-1">
                  {/* Fixed 4-slot grid so icons align across rows */}
                  <div className="grid grid-cols-4 gap-0.5 justify-items-center">
                    {/* Slot 1: Info / Cek RADIUS — always present */}
                    <button onClick={() => checkRadiusStatus(v)} className="p-1 text-purple-500 hover:bg-purple-50 rounded dark:hover:bg-purple-900/30" title="Cek RADIUS">
                      <Info className="w-4 h-4" />
                    </button>
                    {/* Slot 2: Print — always present */}
                    <button onClick={() => handlePrint(v)} className="p-1 text-blue-500 hover:bg-blue-50 rounded dark:hover:bg-blue-900/30" title="Print">
                      <Printer className="w-4 h-4" />
                    </button>
                    {/* Slot 3: Status-dependent action (Disconnect / Disable / Enable) */}
                    {activeVoucherUsernames.includes(v.username?.toUpperCase()) ? (
                      <button
                        onClick={() => disableAndDisconnectVoucher(v.username, v.router_id)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded dark:text-red-400 dark:hover:bg-red-900/30"
                        title="Blokir & Disconnect"
                      >
                        <WifiOff className="w-4 h-4" />
                      </button>
                    ) : v.status === 'available' ? (
                      <button onClick={() => handleDisable(v)} className="p-1 text-orange-500 hover:bg-orange-50 rounded dark:hover:bg-orange-900/30" title="Disable">
                        <Ban className="w-4 h-4" />
                      </button>
                    ) : v.status === 'disabled' ? (
                      <button onClick={() => handleDisable(v)} className="p-1 text-green-500 hover:bg-green-50 rounded dark:hover:bg-green-900/30" title="Enable">
                        <Check className="w-4 h-4" />
                      </button>
                    ) : (
                      <span className="w-6 h-6 inline-block" aria-hidden="true" />
                    )}
                    {/* Slot 4: Delete — only for status=available */}
                    {v.status === 'available' ? (
                      <button onClick={() => handleDelete(v.id)} className="p-1 text-red-500 hover:bg-red-50 rounded dark:hover:bg-red-900/30" title="Hapus">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    ) : (
                      <span className="w-6 h-6 inline-block" aria-hidden="true" />
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {displayedVouchers.length === 0 && (
          <div className="text-center py-8 text-gray-400 text-sm">
            Tidak ada data
          </div>
        )}
      </div>
      )}

      {/* Pagination - Mobile & Desktop */}
      {!loading && totalVouchers > 0 && (
        <div className="bg-white border border-t-0 lg:border-t rounded-b lg:rounded-lg mt-3 lg:mt-0 px-3 py-3 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm dark:bg-slate-800">
          <span className="text-gray-500 dark:text-slate-400">
            {(currentPage-1)*itemsPerPage+1}-{Math.min(currentPage*itemsPerPage, totalVouchers)} dari {totalVouchers}
          </span>
          <div className="flex gap-1">
            <Button onClick={() => setCurrentPage(p => Math.max(p-1, 1))} disabled={currentPage === 1} 
              variant="outline" size="sm" className="h-8 px-3 text-sm">Prev</Button>
            <span className="flex items-center px-3 text-gray-600 dark:text-slate-300">
              {currentPage} / {totalPages}
            </span>
            <Button onClick={() => setCurrentPage(p => Math.min(p+1, totalPages))} disabled={currentPage >= totalPages}
              variant="outline" size="sm" className="h-8 px-3 text-sm">Next</Button>
          </div>
        </div>
      )}

      {/* Generate Modal */}
      <Dialog open={isGenerateOpen} onOpenChange={setIsGenerateOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="text-sm">Generate Voucher</DialogTitle></DialogHeader>
          <div className="space-y-3 text-xs">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Jumlah</Label>
                <Input type="number" value={generateForm.quantity} min="1" max="100"
                  onChange={(e) => setGenerateForm({...generateForm, quantity: parseInt(e.target.value)})}
                  className="h-8" />
              </div>
              <div>
                <Label className="text-xs">Prefix</Label>
                <Input value={generateForm.prefix} maxLength="4"
                  onChange={(e) => setGenerateForm({...generateForm, prefix: e.target.value.toUpperCase()})}
                  className="h-8" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Paket Hotspot *</Label>
              <select value={generateForm.service_plan}
                onChange={(e) => setGenerateForm({...generateForm, service_plan: e.target.value})}
                className="w-full h-8 text-xs border rounded px-2">
                <option value="">Pilih Paket</option>
                {hotspotPlans.map(p => <option key={p.id} value={p.name}>{p.name} - Rp{p.price?.toLocaleString()}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Cabang</Label>
              <select value={generateForm.branch_id}
                onChange={(e) => setGenerateForm({...generateForm, branch_id: e.target.value})}
                className="w-full h-8 text-xs border rounded px-2">
                <option value="">Semua</option>
                {branches.filter(b => b.is_active).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Format Kode</Label>
                <select value={generateForm.code_format}
                  onChange={(e) => setGenerateForm({...generateForm, code_format: e.target.value})}
                  className="w-full h-8 text-xs border rounded px-2">
                  <option value="uppercase_only">Huruf Besar (ABCDEFGH)</option>
                  <option value="lowercase_only">Huruf Kecil (abcdefgh)</option>
                  <option value="mixed_case">Huruf Besar+Kecil (AbCdEfGh)</option>
                  <option value="number_only">Angka Saja (12345678)</option>
                  <option value="all_characters">Semua (Ab1Cd2Ef)</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">Panjang Kode</Label>
                <select value={generateForm.code_length}
                  onChange={(e) => setGenerateForm({...generateForm, code_length: parseInt(e.target.value)})}
                  className="w-full h-8 text-xs border rounded px-2">
                  <option value="6">6 Karakter</option>
                  <option value="8">8 Karakter</option>
                  <option value="10">10 Karakter</option>
                  <option value="12">12 Karakter</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={handleGenerate} disabled={loading} className="flex-1 h-8 text-xs bg-green-600">
                {loading ? 'Generating...' : 'Generate'}
              </Button>
              <Button onClick={() => setIsGenerateOpen(false)} variant="outline" className="flex-1 h-8 text-xs">Batal</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Modal */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-xs">
          <DialogHeader><DialogTitle className="text-sm">Hapus Voucher?</DialogTitle></DialogHeader>
          <p className="text-xs text-gray-600 dark:text-slate-300">Hapus <b className="text-red-600 dark:text-red-400">{selectedVouchers.length}</b> voucher?</p>
          <div className="flex gap-2 pt-2">
            <Button onClick={handleBulkDelete} disabled={loading} variant="destructive" className="flex-1 h-8 text-xs">
              {loading ? 'Menghapus...' : 'Ya, Hapus'}
            </Button>
            <Button onClick={() => setShowDeleteConfirm(false)} variant="outline" className="flex-1 h-8 text-xs">Batal</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete ALL Vouchers Confirm Modal */}
      <Dialog open={showDeleteAllConfirm} onOpenChange={setShowDeleteAllConfirm}>
        <DialogContent className="max-w-md" data-testid="delete-all-modal">
          <DialogHeader>
            <DialogTitle className="text-sm text-red-600 flex items-center gap-2 dark:text-red-400">
              <AlertTriangle className="w-5 h-5" /> Hapus SEMUA Voucher?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-xs">
            <div className="bg-red-50 border border-red-200 rounded p-3 dark:bg-red-900/30">
              <p className="text-red-700 font-medium mb-2 dark:text-red-300">⚠️ PERINGATAN:</p>
              <ul className="text-red-600 space-y-1 list-disc list-inside dark:text-red-400">
                <li>Semua voucher di MongoDB akan dihapus</li>
                <li>Semua voucher di RADIUS server akan dihapus</li>
                <li>Tindakan ini TIDAK BISA dibatalkan</li>
              </ul>
            </div>
            <div className="bg-green-50 border border-green-200 rounded p-3 dark:bg-green-900/30">
              <p className="text-green-700 font-medium mb-1 dark:text-green-300">✅ Data yang AMAN:</p>
              <ul className="text-green-600 space-y-1 list-disc list-inside dark:text-green-400">
                <li>Pelanggan PPPoE tidak akan terhapus</li>
                <li>Data pelanggan di customers collection tetap aman</li>
              </ul>
            </div>
            <p className="text-gray-600 dark:text-slate-300">
              Total voucher saat ini: <b className="text-red-600 dark:text-red-400">{totalVouchers}</b> voucher
            </p>
          </div>
          <div className="flex gap-2 pt-2">
            <Button 
              onClick={deleteAllVouchers} 
              disabled={deleteAllLoading} 
              variant="destructive" 
              className="flex-1 h-9 text-xs"
              data-testid="confirm-delete-all-btn"
            >
              {deleteAllLoading ? 'Menghapus...' : '🗑️ Ya, Hapus Semua Voucher'}
            </Button>
            <Button onClick={() => setShowDeleteAllConfirm(false)} variant="outline" className="flex-1 h-9 text-xs">Batal</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Print Modal */}
      <Dialog open={showPrintModal} onOpenChange={setShowPrintModal}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-auto">
          <DialogHeader><DialogTitle className="text-sm">Print Voucher ({vouchersToPrint.length})</DialogTitle></DialogHeader>
                    <MikrotikGatewaySelector value={selectedMikrotik} onChange={setSelectedMikrotik} />
          <select value={selectedTemplate} onChange={(e) => setSelectedTemplate(e.target.value)}
            className="w-full h-8 text-xs border rounded px-2 mb-2">
            <option value="simple">⚡ Template Ringan: Simple Text (35/hal - RECOMMENDED untuk 1000+ voucher)</option>
            <option value="modern">Template 1: Modern (Default - dengan shadow & gradient)</option>
            <option value="classic">Template 2: Classic Hijau (35 voucher - Minimalis)</option>
            <option value="classic-orange">Template 3: Classic Orange (35 voucher - Warna Logo)</option>
            <option value="classic-blue">Template 4: Classic Biru (35 voucher - Warna Biru)</option>
            <option value="compact">Template 5: Compact (Simple & Clean)</option>
            <option value="elegant">Template 6: Elegant (Premium & Mewah)</option>
            <option value="dense50">Template 7: Dense (50 voucher - 10×5 padat)</option>
            <option value="dense70">Template 8: Ultra Dense (70 voucher - 10×7 sangat padat)</option>
          </select>
          <div className="border rounded overflow-hidden bg-slate-50 px-2 py-2 flex justify-center dark:bg-slate-900">
            <div data-voucher-scale-wrapper style={{ transform: 'scale(0.72)', transformOrigin: 'top center', width: '1122px' }}>
              <VoucherPrintTemplate vouchers={vouchersToPrint} template={selectedTemplate} hotspotGatewayIp={selectedMikrotik?.hotspot_gateway_ip} />
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button onClick={() => window.print()} className="flex-1 h-8 text-xs bg-blue-600">
              <Printer className="w-3 h-3 mr-1" /> Print
            </Button>
            <Button onClick={() => setShowPrintModal(false)} variant="outline" className="flex-1 h-8 text-xs">Batal</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Expiry Check Modal */}
      <Dialog open={showExpiryModal} onOpenChange={setShowExpiryModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="text-sm flex items-center gap-2">
              <Clock className="w-4 h-4" /> Cek Status Expiry Voucher
            </DialogTitle>
          </DialogHeader>
          
          {expiryData && (
            <div className="space-y-4 text-xs">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-green-50 border border-green-200 rounded p-2 text-center dark:bg-green-900/30">
                  <div className="text-lg font-bold text-green-600 dark:text-green-400">{expiryData.summary.active_with_time}</div>
                  <div className="text-green-700 dark:text-green-300">Aktif (ada sisa waktu)</div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded p-2 text-center dark:bg-red-900/30">
                  <div className="text-lg font-bold text-red-600 dark:text-red-400">{expiryData.summary.expired_used}</div>
                  <div className="text-red-700 dark:text-red-300">Sudah Expired</div>
                </div>
                <div className="bg-orange-50 border border-orange-200 rounded p-2 text-center dark:bg-orange-900/30">
                  <div className="text-lg font-bold text-orange-600 dark:text-orange-400">{expiryData.summary.old_unused_30d}</div>
                  <div className="text-orange-700 dark:text-orange-300">Tidak terpakai &gt;30d</div>
                </div>
              </div>

              {/* Active with remaining time */}
              {expiryData.active_with_time?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-green-700 mb-1 flex items-center gap-1 dark:text-green-300">
                    <Check className="w-3 h-3" /> Voucher Aktif (Sisa Waktu)
                  </h4>
                  <div className="bg-white border rounded max-h-40 overflow-auto dark:bg-slate-800">
                    <table className="w-full text-[11px]">
                      <thead className="bg-green-50 sticky top-0 dark:bg-green-900/30">
                        <tr>
                          <th className="px-2 py-1 text-left">Username</th>
                          <th className="px-2 py-1 text-left">Paket</th>
                          <th className="px-2 py-1 text-left">Sisa Waktu</th>
                          <th className="px-2 py-1 text-left">Expired</th>
                        </tr>
                      </thead>
                      <tbody>
                        {expiryData.active_with_time.slice(0, 20).map((v, i) => (
                          <tr key={i} className="border-t">
                            <td className="px-2 py-1 font-mono text-blue-600 dark:text-blue-400">{v.username}</td>
                            <td className="px-2 py-1">{v.service_plan}</td>
                            <td className="px-2 py-1 font-semibold text-green-600 dark:text-green-400">{v.remaining_formatted}</td>
                            <td className="px-2 py-1 text-gray-500 dark:text-slate-400">{v.expires_at?.split('T')[0]}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Expired vouchers */}
              {expiryData.expired_used?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-red-700 mb-1 flex items-center gap-1 dark:text-red-300">
                    <AlertTriangle className="w-3 h-3" /> Voucher Sudah Expired
                  </h4>
                  <div className="bg-white border rounded max-h-40 overflow-auto dark:bg-slate-800">
                    <table className="w-full text-[11px]">
                      <thead className="bg-red-50 sticky top-0 dark:bg-red-900/30">
                        <tr>
                          <th className="px-2 py-1 text-left">Username</th>
                          <th className="px-2 py-1 text-left">Paket</th>
                          <th className="px-2 py-1 text-left">Expired</th>
                          <th className="px-2 py-1 text-left">Sejak</th>
                        </tr>
                      </thead>
                      <tbody>
                        {expiryData.expired_used.slice(0, 20).map((v, i) => (
                          <tr key={i} className="border-t">
                            <td className="px-2 py-1 font-mono text-blue-600 dark:text-blue-400">{v.username}</td>
                            <td className="px-2 py-1">{v.service_plan}</td>
                            <td className="px-2 py-1 text-red-500">{v.expired_at?.split('T')[0]}</td>
                            <td className="px-2 py-1 text-gray-500 dark:text-slate-400">{v.expired_since_days} hari lalu</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Block expired vouchers in RADIUS */}
                  <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded dark:bg-red-900/30">
                    <div className="text-[11px] text-red-700 mb-2 dark:text-red-300">
                      <strong>⚠️ Penting:</strong> Voucher expired masih bisa dipakai login jika belum diblokir di RADIUS server.
                      Klik tombol di bawah untuk memblokir semua voucher expired.
                    </div>
                    <Button onClick={disableExpiredInRadius} disabled={loading} size="sm" className="h-7 text-xs bg-red-500 hover:bg-red-600 w-full">
                      <Ban className="w-3 h-3 mr-1" />
                      {loading ? 'Processing...' : `Blokir ${expiryData.expired_used?.length || 0} Voucher Expired di RADIUS`}
                    </Button>
                  </div>
                </div>
              )}

              {/* Old unused vouchers */}
              {expiryData.old_unused?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-orange-700 mb-1 flex items-center gap-1 dark:text-orange-300">
                    <Clock className="w-3 h-3" /> Voucher Tidak Terpakai &gt;30 Hari
                  </h4>
                  <div className="bg-white border rounded max-h-40 overflow-auto dark:bg-slate-800">
                    <table className="w-full text-[11px]">
                      <thead className="bg-orange-50 sticky top-0 dark:bg-orange-900/30">
                        <tr>
                          <th className="px-2 py-1 text-left">Username</th>
                          <th className="px-2 py-1 text-left">Paket</th>
                          <th className="px-2 py-1 text-left">Dibuat</th>
                          <th className="px-2 py-1 text-left">Umur</th>
                        </tr>
                      </thead>
                      <tbody>
                        {expiryData.old_unused.slice(0, 20).map((v, i) => (
                          <tr key={i} className="border-t">
                            <td className="px-2 py-1 font-mono text-blue-600 dark:text-blue-400">{v.username}</td>
                            <td className="px-2 py-1">{v.service_plan}</td>
                            <td className="px-2 py-1 text-gray-500 dark:text-slate-400">{v.created_at?.split('T')[0]}</td>
                            <td className="px-2 py-1 text-orange-600 font-medium dark:text-orange-400">{v.age_days} hari</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Auto-expire option */}
                  <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded dark:bg-orange-900/30">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[11px] text-orange-700 dark:text-orange-300">Auto-expire voucher tidak terpakai lebih dari</span>
                      <Input 
                        type="number" 
                        value={autoExpireDays} 
                        onChange={(e) => setAutoExpireDays(parseInt(e.target.value) || 30)}
                        className="w-16 h-6 text-xs text-center"
                        min="7"
                        max="365"
                      />
                      <span className="text-[11px] text-orange-700 dark:text-orange-300">hari</span>
                    </div>
                    <Button onClick={runAutoExpire} disabled={loading} size="sm" className="h-7 text-xs bg-orange-500 hover:bg-orange-600 w-full">
                      {loading ? 'Processing...' : `Auto-Expire ${expiryData.old_unused?.length || 0} Voucher`}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
          
          <div className="flex gap-2 pt-2">
            <Button onClick={() => setShowExpiryModal(false)} variant="outline" className="flex-1 h-8 text-xs">Tutup</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* RADIUS Status Modal */}
      <Dialog open={showRadiusModal} onOpenChange={setShowRadiusModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-sm flex items-center gap-2">
              <Info className="w-4 h-4" /> Status Voucher di RADIUS
            </DialogTitle>
          </DialogHeader>
          
          {loadingRadius ? (
            <div className="text-center py-8 text-gray-500 dark:text-slate-400">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
              Memuat data RADIUS...
            </div>
          ) : radiusInfo ? (
            <div className="space-y-3 text-xs">
              {/* Basic info */}
              <div className="bg-gray-50 rounded p-3 dark:bg-slate-900">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <span className="text-gray-500 dark:text-slate-400">Username:</span>
                    <span className="ml-1 font-mono font-semibold text-blue-600 dark:text-blue-400">{radiusInfo.username}</span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-slate-400">Status DB:</span>
                    <span className={`ml-1 font-semibold ${radiusInfo.voucher_status === 'used' ? 'text-green-600' : 'text-blue-600'}`}>
                      {radiusInfo.voucher_status}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-slate-400">Validity:</span>
                    <span className="ml-1 font-semibold">{radiusInfo.validity_display || `${radiusInfo.validity_period} ${radiusInfo.validity_unit}`}</span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-slate-400">Di RADIUS:</span>
                    <span className={`ml-1 font-semibold ${radiusInfo.radius?.exists_in_radius ? 'text-green-600' : 'text-red-600'}`}>
                      {radiusInfo.radius?.exists_in_radius ? 'Ya' : 'Tidak'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Expiry Info */}
              {radiusInfo.expiry_info && (
                <div className={`rounded p-3 ${radiusInfo.is_expired ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
                  <div className="font-semibold mb-1">
                    {radiusInfo.is_expired ? '❌ EXPIRED' : '✅ AKTIF'}
                  </div>
                  {radiusInfo.remaining_time && (
                    <div className="text-lg font-bold text-green-700 dark:text-green-300">
                      Sisa: {radiusInfo.remaining_time.formatted}
                    </div>
                  )}
                  {radiusInfo.expiry_info.expires_at && !radiusInfo.is_expired && (
                    <div className="text-gray-600 dark:text-slate-300">
                      Expired pada: {new Date(radiusInfo.expiry_info.expires_at).toLocaleString('id-ID')}
                    </div>
                  )}
                  {radiusInfo.is_expired && radiusInfo.expiry_info.expired_at && (
                    <div className="text-red-600 dark:text-red-400">
                      Expired sejak: {new Date(radiusInfo.expiry_info.expired_at).toLocaleString('id-ID')}
                    </div>
                  )}
                </div>
              )}

              {/* RADIUS Session Info */}
              {radiusInfo.radius && (
                <div className="bg-purple-50 border border-purple-200 rounded p-3 dark:bg-purple-900/30">
                  <div className="font-semibold text-purple-700 mb-2 dark:text-purple-300">Info RADIUS</div>
                  <div className="grid grid-cols-2 gap-2 text-[11px]">
                    <div>
                      <span className="text-gray-500 dark:text-slate-400">Status Online:</span>
                      <span className={`ml-1 font-semibold ${radiusInfo.radius.is_active ? 'text-green-600' : 'text-gray-500'}`}>
                        {radiusInfo.radius.is_active ? 'Online' : 'Offline'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-slate-400">Total Session:</span>
                      <span className="ml-1">{radiusInfo.radius.total_sessions}</span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-500 dark:text-slate-400">Total Penggunaan:</span>
                      <span className="ml-1 font-medium">{radiusInfo.radius.total_usage_time}</span>
                    </div>
                  </div>

                  {/* Recent sessions */}
                  {radiusInfo.radius.sessions?.length > 0 && (
                    <div className="mt-2">
                      <div className="text-[10px] text-gray-500 mb-1 dark:text-slate-400">Session Terakhir:</div>
                      <div className="max-h-24 overflow-auto bg-white rounded border dark:bg-slate-800">
                        <table className="w-full text-[10px]">
                          <thead className="bg-gray-50 sticky top-0 dark:bg-slate-900">
                            <tr>
                              <th className="px-1 py-0.5 text-left">Start</th>
                              <th className="px-1 py-0.5 text-left">Stop</th>
                              <th className="px-1 py-0.5 text-left">Durasi</th>
                            </tr>
                          </thead>
                          <tbody>
                            {radiusInfo.radius.sessions.map((s, i) => (
                              <tr key={i} className="border-t">
                                <td className="px-1 py-0.5">{s.acctstarttime?.split('T')[0]}</td>
                                <td className="px-1 py-0.5">{s.acctstoptime ? s.acctstoptime.split('T')[0] : <span className="text-green-500">Online</span>}</td>
                                <td className="px-1 py-0.5">{Math.floor((s.acctsessiontime || 0) / 60)} menit</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 dark:text-slate-400">Tidak ada data</div>
          )}
          
          <div className="flex gap-2 pt-2">
            <Button onClick={() => setShowRadiusModal(false)} variant="outline" className="flex-1 h-8 text-xs">Tutup</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManajemenVoucher;
